import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { BaseApi, deleteRequest, getRequest, kratosPublicFetcher, postRequest, staticOptions } from "lib/core/apiClient";
import { useCallback, useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { parseRewardsResponse } from "lib/utils/rewards";
import { RewardsRemovePayload, RewardsUpdatePayload, UseRewardsReturn } from "lib/types/rewards";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { apiURI } from "lib/utils/constants";

const useRewards = (): UseRewardsReturn => {
  const {
    state: { checkoutId, appliedLoyalty },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated },
  } = useAuthContext();

  const [selectedEmail, setSelectedEmail] = useState<string>("");

  const shouldFetchRewards = Boolean(checkoutId)
  const rewardsUrl = `/checkout/${checkoutId}/rewards${selectedEmail ? `?email=${selectedEmail}` : ""}`;

  const {
    data: rewardsResponse,
    isValidating,
    error,
    mutate: mutateRewards,
  } = useSWR(
    () => (shouldFetchRewards ? rewardsUrl : null),
    (url: string) => {
      return isAuthenticated ? getRequest(url, "KRATOS_PRIVATE") : kratosPublicFetcher(url);
    },
    staticOptions,
  );

  const removeRewards = useCallback(
    async (payload: RewardsRemovePayload) => {
      try {
        const response = await deleteRequest(`/checkout/${checkoutId}/rewards`, payload, "KRATOS_PRIVATE");
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`/checkout/${checkoutId}/other-offers`);
        return response;
      } catch (error) {
        console.error(error);
      }
    },
    [checkoutId],
  );

  const updateRewards = useCallback(
    async (payload: RewardsUpdatePayload) => {
      try {
        const response = await postRequest(`/checkout/${checkoutId}/rewards`, payload, "KRATOS_PRIVATE");
        mutate(`/checkout/${checkoutId}/rewards`);
        mutate(`/checkout/${checkoutId}/other-offers`);
        return response;
      } catch (error) {
        console.error(error);
      }
    },
    [checkoutId],
  );

  const parsedResponse = useMemo(
    () => parseRewardsResponse(rewardsResponse, appliedLoyalty),
    [rewardsResponse, appliedLoyalty],
  );

  const isRewardsReady = !shouldFetchRewards || rewardsResponse !== undefined || Boolean(error);

  const returnValue: UseRewardsReturn = {
    state: {
      mode: parsedResponse.mode,
      rewardsConfig: parsedResponse,
      isTileLoading: isValidating,
      isRewardsReady,
      error,
    },
    actions: {
      updateRewards,
      removeRewards,
      setSelectedEmail,
    },
  };

  return returnValue;
};

export default useRewards;
